import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, fmtMoney } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { Gavel, ArrowRightLeft, X, Search, Filter } from "lucide-react";
import { useRoosterResolve, PlayerNameLink } from "../../lib/playerLink";

const ROLE_STYLES = {
  P: "bg-amber-500/25 text-amber-300 border-amber-500/50",
  D: "bg-emerald-500/25 text-emerald-300 border-emerald-500/50",
  C: "bg-blue-500/25 text-blue-300 border-blue-500/50",
  A: "bg-rose-500/25 text-rose-300 border-rose-500/50",
};

export default function LeagueAsta() {
  const { league, teams, isAdmin } = useLeague();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [search, setSearch] = useState("");
  const [transferPlayer, setTransferPlayer] = useState(null);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sortBy, setSortBy] = useState("base_desc"); // base_desc | base_asc | qta_desc | qta_asc | name_asc

  const protectedPct = league.settings?.protected_salary_pct ?? 0.40;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leagues/${league.league_id}/players`);
      setPlayers(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [league.league_id]);

  // Iter 68: nome cliccabile → storico giocatore
  const roosterMap = useRoosterResolve(players.map(p => p.name));

  // Filtro asta furto:
  //   - Solo giocatori attivi (team_id != null)
  //   - Escludi blindati
  //   - Escludi svincolati e ceduti
  //   - Escludi con stipendio >= protected_pct * quotazione_attuale
  const auctionable = useMemo(() => {
    return players.filter(p => {
      if (!p.team_id) return false;
      if (p.status === "svincolato" || p.status === "ceduto_esterno" || p.status === "blindato") return false;
      const qa = Number(p.quotazione_attuale || 0);
      const salary = Number(p.salary || 0);
      if (qa > 0 && salary / qa >= protectedPct) return false;
      return true;
    });
  }, [players, protectedPct]);

  // Valore d'asta base = prezzo_pagato + salary (approssimazione crediti in base a stipendio)
  const baseAuction = (p) => Math.round(Number(p.prezzo_pagato || 0) + Number(p.salary || 0));

  const filtered = useMemo(() => {
    let list = auctionable;
    if (roleFilter) list = list.filter(p => p.role === roleFilter);
    if (teamFilter) list = list.filter(p => p.team_id === teamFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(p => (p.name || "").toLowerCase().includes(s));
    }
    const pmin = priceMin === "" ? null : Number(priceMin);
    const pmax = priceMax === "" ? null : Number(priceMax);
    if (pmin != null && !isNaN(pmin)) list = list.filter(p => baseAuction(p) >= pmin);
    if (pmax != null && !isNaN(pmax)) list = list.filter(p => baseAuction(p) <= pmax);
    // Sorting
    const sorters = {
      base_desc: (a, b) => baseAuction(b) - baseAuction(a),
      base_asc: (a, b) => baseAuction(a) - baseAuction(b),
      qta_desc: (a, b) => Number(b.quotazione_attuale || 0) - Number(a.quotazione_attuale || 0),
      qta_asc: (a, b) => Number(a.quotazione_attuale || 0) - Number(b.quotazione_attuale || 0),
      name_asc: (a, b) => (a.name || "").localeCompare(b.name || ""),
    };
    return [...list].sort(sorters[sortBy] || sorters.base_desc);
  }, [auctionable, roleFilter, teamFilter, search, priceMin, priceMax, sortBy]);

  const teamName = (tid) => teams.find(t => t.team_id === tid)?.name || "—";

  const counts = useMemo(() => {
    const c = { P: 0, D: 0, C: 0, A: 0, total_base: 0 };
    for (const p of auctionable) {
      c[p.role] = (c[p.role] || 0) + 1;
      c.total_base += baseAuction(p);
    }
    return c;
  }, [auctionable]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold flex items-center gap-2">
            <Gavel size={14}/> Asta furto di luglio
          </div>
          <h2 className="text-3xl font-black tracking-tight">Cartellini disponibili</h2>
          <p className="text-slate-400 text-sm mt-1">
            Tutti i giocatori in rosa <strong className="text-slate-300">esclusi</strong> blindati e giocatori con stipendio ≥ <strong className="text-amber-300">{(protectedPct*100).toFixed(0)}%</strong> della Qt.A.
          </p>
        </div>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard emoji="🎯" label="Disponibili" value={auctionable.length} color="amber"/>
        <StatCard emoji="🧤" label="Portieri" value={counts.P} color="amber"/>
        <StatCard emoji="🛡️" label="Difensori" value={counts.D} color="emerald"/>
        <StatCard emoji="⚙️" label="Centrocamp." value={counts.C} color="blue"/>
        <StatCard emoji="⚔️" label="Attaccanti" value={counts.A} color="rose"/>
      </div>

      {/* Guida uso */}
      <div className="border border-amber-500/30 rounded-3xl bg-gradient-to-br from-amber-500/5 to-slate-900 p-4 shadow-xl shadow-black/40">
        <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">📖 Come si usa</div>
        <ol className="text-xs text-slate-300 mt-1 space-y-1 list-decimal ml-4">
          <li>Ogni giocatore ha un <strong className="text-amber-300">valore base d&apos;asta</strong> = prezzo pagato + stipendio attuale.</li>
          <li>L&apos;asta si svolge <strong className="text-amber-300">fuori dall&apos;app</strong> (Fantabuz/Fantalab).</li>
          <li>Al termine l&apos;admin clicca <strong className="text-purple-300">Sposta al vincitore</strong> su ogni giocatore acquisito e inserisce il <strong className="text-emerald-300">prezzo finale</strong> e il nuovo stipendio.</li>
          <li>I crediti vengono <strong className="text-rose-300">scalati dall&apos;acquirente</strong> e <strong className="text-emerald-300">accreditati al venditore</strong>.</li>
        </ol>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-slate-500"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca giocatore…"
            className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl px-3 py-2 text-sm"
            data-testid="asta-search"/>
        </div>
        <select value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} data-testid="asta-role-filter"
          className="bg-slate-900 border border-slate-800 rounded-2xl px-3 py-2 text-sm">
          <option value="">Tutti i ruoli</option>
          <option value="P">Portieri</option>
          <option value="D">Difensori</option>
          <option value="C">Centrocampisti</option>
          <option value="A">Attaccanti</option>
        </select>
        <select value={teamFilter} onChange={e=>setTeamFilter(e.target.value)} data-testid="asta-team-filter"
          className="bg-slate-900 border border-slate-800 rounded-2xl px-3 py-2 text-sm">
          <option value="">Tutte le squadre</option>
          {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
        </select>
        <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-2xl px-2 py-1" title="Filtra per valore base d'asta">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 pl-1">Prezzo</span>
          <input type="number" min="0" step="1" value={priceMin} onChange={e=>setPriceMin(e.target.value)}
            data-testid="asta-price-min"
            placeholder="min"
            className="w-16 bg-transparent text-sm text-slate-100 px-1 py-1 focus:outline-none placeholder:text-slate-600"/>
          <span className="text-slate-600 text-xs">–</span>
          <input type="number" min="0" step="1" value={priceMax} onChange={e=>setPriceMax(e.target.value)}
            data-testid="asta-price-max"
            placeholder="max"
            className="w-16 bg-transparent text-sm text-slate-100 px-1 py-1 focus:outline-none placeholder:text-slate-600"/>
          {(priceMin !== "" || priceMax !== "") && (
            <button onClick={() => { setPriceMin(""); setPriceMax(""); }}
              data-testid="asta-price-clear"
              title="Rimuovi filtro prezzo"
              className="p-1 rounded hover:bg-slate-800 text-slate-500 hover:text-rose-300">
              <X size={12}/>
            </button>
          )}
        </div>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)} data-testid="asta-sort-by"
          title="Ordina risultati"
          className="bg-slate-900 border border-slate-800 rounded-2xl px-3 py-2 text-sm text-slate-300">
          <option value="base_desc">💰 Prezzo base ↓</option>
          <option value="base_asc">💰 Prezzo base ↑</option>
          <option value="qta_desc">📊 Qt.A ↓</option>
          <option value="qta_asc">📊 Qt.A ↑</option>
          <option value="name_asc">🔤 Nome A–Z</option>
        </select>
      </div>

      {loading ? <div className="text-slate-500 py-12 text-center">Caricamento…</div> :
      filtered.length === 0 ? (
        <div className="border border-dashed border-slate-800 rounded-3xl p-12 text-center text-slate-500">
          <div className="text-4xl mb-2">🎯</div>
          Nessun giocatore all&apos;asta con questi filtri.
        </div>
      ) : (
        <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 overflow-hidden shadow-2xl shadow-black/40">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-slate-950/70">
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-3 py-3">R</th>
                  <th className="px-3 py-3">Giocatore</th>
                  <th className="px-3 py-3">Club</th>
                  <th className="px-3 py-3">Squadra attuale</th>
                  <th className="px-3 py-3 text-right">Qt.A</th>
                  <th className="px-3 py-3 text-right">Stipendio</th>
                  <th className="px-3 py-3 text-right">🎯 Base asta</th>
                  {isAdmin && <th className="px-3 py-3 text-right">⚙️</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.player_id} className="border-t border-slate-800 hover:bg-amber-500/5 transition-colors" data-testid={`asta-row-${p.player_id}`}>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-black text-[12px] border shadow-md ${ROLE_STYLES[p.role] || "bg-slate-700"}`}>{p.role}</span>
                    </td>
                    <td className="px-3 py-2.5 font-bold">
                      <PlayerNameLink name={p.name} fantaId={p.fanta_id} roosterMap={roosterMap} testid={`asta-player-name-${p.player_id}`}/>
                      {p.roles_mantra?.length > 0 && (
                        <span className="ml-2 text-[9px] text-slate-500 font-normal">{p.roles_mantra.join("·")}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-slate-400">{p.real_team || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-300">{teamName(p.team_id)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmtMoney(p.quotazione_attuale)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-rose-300">{fmtMoney(p.salary)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-black text-amber-300">{fmtMoney(baseAuction(p))}</td>
                    {isAdmin && (
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={()=>setTransferPlayer(p)} data-testid={`asta-transfer-${p.player_id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1.5 rounded-full border border-purple-500/50 text-purple-300 hover:bg-purple-500/20 transition-all hover:scale-105">
                          <ArrowRightLeft size={11}/> Sposta al vincitore
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {transferPlayer && (
        <AstaTransferModal
          player={transferPlayer}
          teams={teams}
          leagueId={league.league_id}
          baseValue={baseAuction(transferPlayer)}
          onClose={()=>setTransferPlayer(null)}
          onDone={async ()=>{ setTransferPlayer(null); await load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ emoji, label, value, color }) {
  const colorMap = {
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-300",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
    blue: "border-blue-500/30 bg-blue-500/5 text-blue-300",
    rose: "border-rose-500/30 bg-rose-500/5 text-rose-300",
  }[color] || "";
  return (
    <div className={`rounded-3xl border ${colorMap} p-3 shadow-xl shadow-black/40`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">{emoji} {label}</div>
      <div className="text-2xl font-mono font-black text-white mt-1">{value}</div>
    </div>
  );
}

function AstaTransferModal({ player, teams, leagueId, baseValue, onClose, onDone }) {
  const [toTeamId, setToTeamId] = useState("");
  const [finalPrice, setFinalPrice] = useState(baseValue);
  const [newSalary, setNewSalary] = useState(Math.round((player.quotazione_attuale || 0) * 0.10));
  const [allowOverdraft, setAllowOverdraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const others = teams.filter(t => t.team_id !== player.team_id);
  const targetTeam = teams.find(t => t.team_id === toTeamId);
  const targetBudgetAfter = targetTeam ? Number(targetTeam.budget || 0) - Number(finalPrice || 0) : null;

  const submit = async () => {
    if (!toTeamId) { toast.error("Seleziona la squadra vincitrice"); return; }
    setSubmitting(true);
    try {
      await api.post(`/leagues/${leagueId}/players/${player.player_id}/transfer`, {
        to_team_id: toTeamId,
        amount: Number(finalPrice || 0),
        new_salary: Number(newSalary || 0),
        allow_overdraft: allowOverdraft,
      });
      toast.success(`🎯 ${player.name} → ${targetTeam.name} · ${fmtMoney(finalPrice)} cr`);
      await onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 shadow-2xl shadow-black/80 p-6 space-y-4"
        data-testid="asta-transfer-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold">🎯 Sposta al vincitore d&apos;asta</div>
            <h3 className="text-2xl font-black mt-1">{player.name}</h3>
            <div className="text-sm text-slate-400 mt-1">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full font-black text-[10px] border ${ROLE_STYLES[player.role] || "bg-slate-700"} mr-1`}>{player.role}</span>
              {player.real_team} · Base asta: <strong className="text-amber-300">{fmtMoney(baseValue)} cr</strong>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white p-1"><X size={20}/></button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">🏆 Squadra vincitrice</label>
          <select value={toTeamId} onChange={e=>setToTeamId(e.target.value)} data-testid="asta-to-team"
            className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2.5 text-sm">
            <option value="">— seleziona vincitore —</option>
            {others.map(t => <option key={t.team_id} value={t.team_id}>⚽ {t.name} (budget {fmtMoney(t.budget)} cr)</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">💰 Prezzo finale</label>
            <input type="number" min="0" step="1" value={finalPrice} onChange={e=>setFinalPrice(e.target.value)} data-testid="asta-final-price"
              className="w-full bg-slate-950 border border-amber-500/30 rounded-2xl px-3 py-2.5 text-sm font-mono font-black text-amber-300"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">💵 Nuovo stipendio</label>
            <input type="number" min="0" step="1" value={newSalary} onChange={e=>setNewSalary(e.target.value)} data-testid="asta-new-salary"
              className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2.5 text-sm font-mono"/>
          </div>
        </div>

        {targetTeam && (
          <div className={`text-[11px] rounded-2xl border p-3 flex items-center justify-between ${targetBudgetAfter < 0 ? "border-rose-500/50 bg-rose-500/10" : "border-emerald-500/30 bg-emerald-500/5"}`}>
            <span className="text-slate-400">Budget {targetTeam.name} dopo l&apos;asta:</span>
            <span className={`font-mono font-black ${targetBudgetAfter < 0 ? "text-rose-300" : "text-emerald-300"}`}>{fmtMoney(targetBudgetAfter)} cr</span>
          </div>
        )}
        {targetBudgetAfter !== null && targetBudgetAfter < 0 && (
          <label className="flex items-center gap-2 text-[11px] text-rose-300 cursor-pointer" data-testid="asta-overdraft">
            <input type="checkbox" checked={allowOverdraft} onChange={e=>setAllowOverdraft(e.target.checked)}/>
            ⚠️ Consenti comunque budget negativo
          </label>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-2xl border border-slate-700 text-slate-400 hover:text-white text-sm font-bold uppercase">Annulla</button>
          <button onClick={submit} disabled={submitting || !toTeamId} data-testid="asta-submit"
            className="px-6 py-2 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 font-black uppercase text-sm shadow-lg shadow-amber-500/40 disabled:opacity-50">
            {submitting ? "Applico…" : "🎯 Sposta"}
          </button>
        </div>
      </div>
    </div>
  );
}
